import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { AlertTriangle, CalendarDays, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { localDateString, formatPrettyDate } from "@/lib/app-types";
import { useInputKeyboardScroll } from "@/hooks/use-input-keyboard-scroll";
import { BottomSheet } from "./bottom-sheet";
import { RippleButton } from "./ripple-button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  DEFAULT_QUESTION_TARGET,
  QUESTION_SOURCES,
  QUESTION_SUBJECTS,
  SCHEDULE_GAPS,
  buildSelfSchedule,
  subjectMeta,
} from "@/lib/question-plan";

export type QuestionSetInput = {
  subject: string;
  chapter: string;
  topic: string | null;
  source: string;
  target: number | null;
  scheduleMode: "auto" | "self";
  self: { startDate: string; gapDays: number; perSession: number; endDate: string | null } | null;
};

function SubjectSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = subjectMeta(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "mt-2.5 flex w-full items-center justify-between rounded-2xl border border-hairline bg-surface px-4 py-3 text-left text-[15px] font-medium outline-none transition-colors focus:border-primary/50",
          )}
        >
          <span>{selected.label}</span>
          <ChevronDown
            className={cn(
              "size-4 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[100] w-[min(calc(100vw-2rem),20rem)] p-3"
      >
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Quick select
        </p>
        <div className="mb-2 grid grid-cols-4 gap-2">
          {QUESTION_SUBJECTS.slice(0, 4).map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                onChange(s.key);
                setOpen(false);
              }}
              className={cn(
                "rounded-xl px-1 py-2 text-[11px] font-semibold transition-colors",
                value === s.key
                  ? "text-primary-foreground [background-image:var(--gradient-brand)]"
                  : "border border-hairline bg-surface text-muted-foreground",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="my-2 h-px bg-hairline" />
        <div className="space-y-1">
          {QUESTION_SUBJECTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                onChange(s.key);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-[14px] transition-colors",
                value === s.key
                  ? "bg-primary/10 font-semibold text-primary"
                  : "text-foreground hover:bg-surface",
              )}
            >
              <span className="flex items-center gap-2">
                <span>{s.emoji}</span>
                <span>{s.label}</span>
              </span>
              {value === s.key && <Check className="size-4 text-primary" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SourceSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "mt-2.5 flex w-full items-center justify-between rounded-2xl border border-hairline bg-surface px-4 py-3 text-left text-[15px] font-medium outline-none transition-colors focus:border-primary/50",
          )}
        >
          <span className="flex items-center gap-2">
            <span className="text-[15px]">📄</span>
            <span className={value ? "text-foreground" : "text-muted-foreground"}>
              {value || "Select source"}
            </span>
          </span>
          <ChevronDown
            className={cn(
              "size-4 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[100] w-[min(calc(100vw-2rem),20rem)] p-3"
      >
        <div className="space-y-1">
          {QUESTION_SOURCES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                onChange(s);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-[14px] transition-colors",
                value === s
                  ? "bg-primary/10 font-semibold text-primary"
                  : "text-foreground hover:bg-surface",
              )}
            >
              <span className="flex items-center gap-2">
                <span className="text-[15px]">📄</span>
                <span>{s}</span>
              </span>
              {value === s && <Check className="size-4 text-primary" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const labelClass =
  "text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
const fieldClass =
  "mt-2.5 w-full rounded-2xl border border-hairline bg-surface px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/50";

export function QuestionSetSheet({
  open,
  busy,
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (input: QuestionSetInput) => void;
}) {
  const [subject, setSubject] = useState<string>("Physics");
  const [chapter, setChapter] = useState("");
  const [topic, setTopic] = useState("");
  const [source, setSource] = useState<string>("");
  const [target, setTarget] = useState(`${DEFAULT_QUESTION_TARGET}`);
  const [mode, setMode] = useState<"auto" | "self">("auto");
  const [startDate, setStartDate] = useState(localDateString());
  const [gap, setGap] = useState(1);
  const [perSession, setPerSession] = useState("20");
  const [useEnd, setUseEnd] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const chapterScroll = useInputKeyboardScroll<HTMLInputElement>();

  useEffect(() => {
    if (!open) return;
    setSubject("Physics");
    setChapter("");
    setTopic("");
    setSource("");
    setTarget(`${DEFAULT_QUESTION_TARGET}`);
    setMode("auto");
    setStartDate(localDateString());
    setGap(1);
    setPerSession("20");
    setUseEnd(false);
    setEndDate("");
    setError(null);
  }, [open]);

  const totalQuestions = Math.max(
    1,
    Math.min(Number(target) || DEFAULT_QUESTION_TARGET, 5000),
  );
  const per = Math.max(1, Math.min(Number(perSession) || 1, 500));

  const preview = useMemo(
    () =>
      buildSelfSchedule({
        total: totalQuestions,
        startDate,
        gapDays: gap,
        perSession: per,
        endDate: useEnd && endDate ? endDate : null,
      }),
    [totalQuestions, startDate, gap, per, useEnd, endDate],
  );

  function submit() {
    if (!chapter.trim()) {
      setError("Add a chapter name.");
      return;
    }
    if (!source) {
      setError("Choose where these questions come from.");
      return;
    }
    setError(null);
    onSubmit({
      subject,
      chapter: chapter.trim(),
      topic: topic.trim() || null,
      source,
      target: mode === "self" ? totalQuestions : null,
      scheduleMode: mode,
      self:
        mode === "self"
          ? {
              startDate,
              gapDays: gap,
              perSession: per,
              endDate: useEnd && endDate ? endDate : null,
            }
          : null,
    });
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      tall
      title="New question set"
      subtitle="Track questions by chapter and source."
      footer={
        <RippleButton className="w-full" disabled={busy} onClick={submit}>
          Create question set
        </RippleButton>
      }
    >
      <div className="space-y-5">
        <div>
          <p className={labelClass}>Subject *</p>
          <SubjectSelect value={subject} onChange={setSubject} />
        </div>

        <div>
          <p className={labelClass}>Chapter name</p>
          <input
            ref={chapterScroll.ref}
            onFocus={chapterScroll.onFocus}
            value={chapter}
            onChange={(e) => setChapter(e.target.value)}
            placeholder="e.g. Electrostatics"
            maxLength={120}
            className={fieldClass}
          />
        </div>

        <div>
          <p className={labelClass}>Topic (optional)</p>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Gauss law"
            maxLength={120}
            className={fieldClass}
          />
        </div>

        <div>
          <p className={labelClass}>Question source *</p>
          <SourceSelect value={source} onChange={setSource} />
        </div>

        <div>
          <p className={labelClass}>Schedule</p>
          <div className="mt-2.5 grid grid-cols-2 gap-2">
            {(
              [
                { key: "auto", label: "Auto Schedule", hint: "180 questions, day 1–60" },
                { key: "self", label: "Self Schedule", hint: "You choose the pace" },
              ] as const
            ).map((item) => {
              const active = mode === item.key;
              return (
                <motion.button
                  key={item.key}
                  type="button"
                  whileTap={{ scale: 0.96 }}
                  onClick={() => setMode(item.key)}
                  className={cn(
                    "rounded-2xl px-3 py-3 text-left transition-colors",
                    active
                      ? "text-primary-foreground [background-image:var(--gradient-brand)]"
                      : "border border-hairline bg-surface",
                  )}
                >
                  <span className="block text-[13px] font-bold">{item.label}</span>
                  <span
                    className={cn(
                      "mt-0.5 block text-[11px]",
                      active ? "opacity-80" : "text-muted-foreground",
                    )}
                  >
                    {item.hint}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {mode === "self" ? (
          <div className="space-y-5 rounded-[24px] border border-hairline bg-surface p-4">
            <div>
              <p className={labelClass}>Target questions</p>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={5000}
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className={fieldClass}
              />
            </div>

            <div>
              <p className={labelClass}>Start date</p>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={cn(fieldClass, "appearance-none")}
              />
            </div>

            <div>
              <p className={labelClass}>Schedule frequency</p>
              <div className="mt-2.5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {SCHEDULE_GAPS.map((item) => {
                  const active = gap === item.days;
                  return (
                    <motion.button
                      key={item.days}
                      type="button"
                      whileTap={{ scale: 0.94 }}
                      onClick={() => setGap(item.days)}
                      className={cn(
                        "rounded-2xl px-2 py-2.5 text-[12px] font-semibold transition-colors",
                        active
                          ? "text-primary-foreground [background-image:var(--gradient-brand)]"
                          : "border border-hairline bg-background text-muted-foreground",
                      )}
                    >
                      {item.label}
                    </motion.button>
                  );
                })}
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <span className="text-[12px] text-muted-foreground">Custom gap</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={30}
                  value={gap}
                  onChange={(e) =>
                    setGap(Math.max(1, Math.min(Number(e.target.value) || 1, 30)))
                  }
                  className="w-20 rounded-xl border border-hairline bg-background px-3 py-2 text-[14px] outline-none focus:border-primary/50"
                />
                <span className="text-[12px] text-muted-foreground">days</span>
              </div>
            </div>

            <div>
              <p className={labelClass}>Questions per scheduled day</p>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={500}
                value={perSession}
                onChange={(e) => setPerSession(e.target.value)}
                className={fieldClass}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-[13px] font-semibold">
                <input
                  type="checkbox"
                  checked={useEnd}
                  onChange={(e) => setUseEnd(e.target.checked)}
                  className="size-4 accent-[var(--color-primary,currentColor)]"
                />
                Set an end date (optional)
              </label>
              {useEnd ? (
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={cn(fieldClass, "appearance-none")}
                />
              ) : null}
            </div>

            <div className="rounded-2xl border border-hairline bg-background p-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Total questions", value: `${totalQuestions}` },
                  { label: "Per session", value: `${per}` },
                  {
                    label: "Frequency",
                    value:
                      SCHEDULE_GAPS.find((g) => g.days === gap)?.label ?? `Every ${gap} days`,
                  },
                  { label: "Sessions", value: `${preview.sessionCount}` },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl bg-surface px-3 py-2">
                    <p className="text-[13px] font-extrabold tabular-nums">{stat.value}</p>
                    <p className="text-[10px] text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-2.5 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <CalendarDays className="size-3.5" /> Finishes on{" "}
                {preview.completionDate ? formatPrettyDate(preview.completionDate) : "—"}
              </p>
              {preview.warning ? (
                <p className="mt-2 flex items-start gap-1.5 rounded-xl border border-warning/25 bg-warning/10 px-3 py-2 text-[12px] text-warning">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <span>{preview.warning}</span>
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="rounded-2xl border border-hairline bg-surface px-4 py-3 text-[12px] text-muted-foreground">
            Auto Schedule builds the standard {DEFAULT_QUESTION_TARGET}-question plan across day
            1, 3, 7, 15, 30 and 60 — exactly as before.
          </p>
        )}

        {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
      </div>
    </BottomSheet>
  );
}
